import { createContext, useContext, useCallback, useState } from "react";
import { TopHeadValidateUrl, AddImageUrl } from "../constants";

const ScanImageContext = createContext();

// Export the hook to access the UID context
export const useScanImage = () => {
  return useContext(ScanImageContext);
};

// Provider component to wrap around the app
export const ScanImageProvider = ({ children }) => {
  const [images, setImages] = useState({});
  const [frontAnimationImage, setFrontAnimationImage] = useState({
    imageUrl: "",
    keypoints: [],
    width: 2000,
    height: 3000,
  });

  const resetScanImage = () => {
    setImages({});
    setFrontAnimationImage({
      imageUrl: "",
      keypoints: [],
      width: 2000,
      height: 3000,
    });
  };

  const addFrontImage = (
    imageData,
    meshKeypoints = null,
    imageWidth = null,
    imageHeight = null,
  ) => {
    setImages((prevData) => ({ ...prevData, front: imageData }));
    setFrontAnimationImage((prevData) => ({
      ...prevData,
      imageUrl: imageData,
      keypoints: meshKeypoints,
      width: imageWidth,
      height: imageHeight,
    }));
  };

  const addSideImage = (faceSide, imageData) => {
    setImages((prevData) => ({ ...prevData, [faceSide]: imageData }));
  };

  const uploadImages = async (apiToken) => {
    try {
      for (const [faceSide, imageData] of Object.entries(images)) {
        const imageContent = imageData
          .toString()
          .replace(/^data:image\/jpeg;base64,/, "");
        const response = await fetch(AddImageUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: apiToken,
          },
          body: JSON.stringify({
            image: imageContent,
            faceSide: faceSide,
          }),
        });
        if (!response.ok) {
          throw new Error("Failed to upload image");
        }
      }
      setImages({});
      return true;
    } catch (error) {
      return false;
    }
  };

  const validateTopHeadImage = useCallback(async () => {
    // In case topHead is not detected it should raise an error
    if (images.top === undefined) return { ok: false, isHeadDetected: false };

    try {
      const imageContent = images.top
        .toString()
        .replace(/^data:image\/jpeg;base64,/, "");
      const response = await fetch(TopHeadValidateUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({
          imageBase64: imageContent,
        }).toString(),
      });
      if (!response.ok) {
        throw new Error("Failed to validate top head image");
      }
      const responseData = await response.json();
      return { ok: true, isHeadDetected: responseData?.headDetected ?? false };
    } catch (error) {
      return { ok: false, isHeadDetected: false };
    }
  }, [images]);

  const imagesCaptured = Object.keys(images).length > 0;

  return (
    <ScanImageContext.Provider
      value={{
        frontAnimationImage,
        imagesCaptured,
        resetScanImage,
        addFrontImage,
        addSideImage,
        uploadImages,
        validateTopHeadImage,
      }}
    >
      {children}
    </ScanImageContext.Provider>
  );
};
